import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import {
  MaterialCategory,
  MaterialCategoryCreateInput,
  MaterialCategoryUpdateInput,
  CategoryType,
} from '@models/warehouse.model';
import { getCategoryIcon } from '@utils/categoryIcons';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaletteIcon from '@mui/icons-material/Palette';

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MaterialCategoryCreateInput | MaterialCategoryUpdateInput) => Promise<void>;
  category?: MaterialCategory | null;
  isLoading?: boolean;
  defaultType?: CategoryType;
  globalCategories?: MaterialCategory[];
}

const ICON_OPTIONS: { value: string; label: string }[] = [
  { value: 'home', label: 'Construction' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'landscape', label: 'Aggregates' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical_services', label: 'Electrical' },
  { value: 'forest', label: 'Wood & Lumber' },
  { value: 'water_drop', label: 'Water / Pool' },
  { value: 'build', label: 'Tools' },
  { value: 'construction', label: 'Equipment' },
  { value: 'local_shipping', label: 'Shipping' },
  { value: 'inventory_2', label: 'Inventory' },
  { value: 'category', label: 'General' },
];

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', bg: 'bg-blue-500' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-500' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500' },
  { value: 'green', label: 'Green', bg: 'bg-green-500' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500' },
];

const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  green: 'bg-green-100 text-green-600',
  indigo: 'bg-indigo-100 text-indigo-600',
};

const TYPE_OPTIONS: { value: CategoryType; label: string }[] = [
  { value: CategoryType.MATERIAL, label: 'Material' },
  { value: CategoryType.EQUIPMENT, label: 'Equipment' },
  { value: CategoryType.BOTH, label: 'Both' },
];

export const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  category,
  isLoading = false,
  defaultType = CategoryType.MATERIAL,
  globalCategories = [],
}) => {
  const isEditing = !!category;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('category');
  const [color, setColor] = useState('blue');
  const [type, setType] = useState<CategoryType>(CategoryType.MATERIAL);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState('');
  const [nameError, setNameError] = useState('');

  // Build a set of lowercase global category names for fast lookup
  const globalCategoryNames = useMemo(
    () => new Set(globalCategories.map((c) => c.name.trim().toLowerCase())),
    [globalCategories],
  );

  // Validate name against global categories (case-insensitive exact match)
  const validateName = (value: string): string => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed && globalCategoryNames.has(trimmed)) {
      const matched = globalCategories.find(
        (c) => c.name.trim().toLowerCase() === trimmed,
      );
      return `"${matched?.name}" already exists as a global category. Use a different name like "${matched?.name} 2".`;
    }
    return '';
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(validateName(value));
  };

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      if (category) {
        setName(category.name || '');
        setDescription(category.description || '');
        setIcon(category.icon || 'category');
        setColor(category.color || 'blue');
        setType(category.type || CategoryType.MATERIAL);
        setIsActive(category.isActive ?? true);
        setSortOrder(category.sortOrder?.toString() || '');
      } else {
        setName('');
        setDescription('');
        setIcon('category');
        setColor('blue');
        setType(defaultType);
        setIsActive(true);
        setSortOrder('');
      }
      setNameError('');
    }
  }, [isOpen, category, defaultType]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const error = validateName(name);
    if (error) {
      setNameError(error);
      return;
    }

    const baseData = {
      name: name.trim(),
      description: description.trim() || undefined,
      icon: icon || undefined,
      color: color || undefined,
      type,
      isActive,
      sortOrder: sortOrder ? parseInt(sortOrder, 10) : undefined,
    };

    if (isEditing && category) {
      await onSubmit({ categoryId: category.categoryId, ...baseData });
    } else {
      await onSubmit(baseData as MaterialCategoryCreateInput);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Category' : 'Add Category'}
      confirmText={isEditing ? 'Save Changes' : 'Add Category'}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!name.trim() || !!nameError}
      size="medium"
    >
      <div className="flex flex-col gap-5">
        {/* Icon & Color */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PaletteIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Appearance
          </h3>

          {/* Preview */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              COLOR_MAP[color] || COLOR_MAP.blue
            }`}>
              {getCategoryIcon(icon, { sx: { fontSize: 28 } })}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Preview</p>
              <p className="text-xs text-gray-500">
                {ICON_OPTIONS.find((o) => o.value === icon)?.label || 'General'} &middot; {COLOR_OPTIONS.find((o) => o.value === color)?.label || 'Blue'}
              </p>
            </div>
          </div>

          {/* Icon Grid */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="grid grid-cols-6 gap-2">
              {ICON_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setIcon(opt.value)}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${
                    icon === opt.value
                      ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  title={opt.label}
                >
                  <span className={icon === opt.value ? 'text-indigo-600' : 'text-gray-500'}>
                    {getCategoryIcon(opt.value, { sx: { fontSize: 22 } })}
                  </span>
                  <span className={`text-[10px] mt-1 truncate w-full text-center ${
                    icon === opt.value ? 'text-indigo-600 font-medium' : 'text-gray-400'
                  }`}>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex gap-2">
              {COLOR_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setColor(opt.value)}
                  className={`w-8 h-8 rounded-full ${opt.bg} transition-all ${
                    color === opt.value
                      ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110'
                      : 'hover:scale-105'
                  }`}
                  title={opt.label}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Details
          </h3>
          <div className="flex flex-col gap-4">
            <FormGroup label="Category Name *" htmlFor="categoryName">
              <Input
                id="categoryName"
                value={name}
                onChange={handleNameChange}
                placeholder="e.g., Basic Construction"
                autoFocus
              />
              {nameError && (
                <p className="mt-1 text-xs text-red-600">{nameError}</p>
              )}
            </FormGroup>

            <FormGroup label="Description" htmlFor="categoryDescription">
              <textarea
                id="categoryDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this category..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows={2}
              />
            </FormGroup>

            <FormGroup label="Category Type *" htmlFor="categoryType">
              <ReactSelect
                options={TYPE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                value={{
                  value: type,
                  label: TYPE_OPTIONS.find((o) => o.value === type)?.label || '',
                }}
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setType(option.value as CategoryType);
                  }
                }}
                placeholder="Select type..."
              />
            </FormGroup>

            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Sort Order" htmlFor="categorySortOrder">
                <Input
                  id="categorySortOrder"
                  type="number"
                  min="0"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">Lower numbers appear first</p>
              </FormGroup>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="categoryStatus"
                      checked={isActive}
                      onChange={() => setIsActive(true)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="categoryStatus"
                      checked={!isActive}
                      onChange={() => setIsActive(false)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CategoryFormModal;
